import sessionReader from '../store/readHelpers/SessionReader';
import localCacheService from './LocalCache';

let instance = null;

/**
 * @param {array} unwantedList The substrings we do not want to find in str.
 * @param {string} str
 * @return {boolean}
 */
function doesNotIncludeAnyOf(unwantedList, str) {
    return !unwantedList.some(unwantedItem => str.includes(unwantedItem));
}

export class StateCache {
    static setInstance(newInstance) {
        instance = newInstance;
    }

    constructor() {
        this.localCache = localCacheService.getLocalCacheInstance();
        this.questionSetIdentifier = sessionReader.getQuestionSetIdentifier();
    }

    setQuestionResponse(contentRevisionId, questionResponse) {
        const clonedResponse = Object.assign({}, questionResponse);
        const key = this.getKey('response', contentRevisionId);
        const json = JSON.stringify(clonedResponse);

        this.localCache.set(key, json);
    }

    getQuestionResponse(contentRevisionId) {
        const key = this.getKey('response', contentRevisionId);
        const cachedResponse = this.localCache.get(key);

        if (cachedResponse) {
            return JSON.parse(cachedResponse);
        }

        return null;
    }

    removeQuestionResponse(contentRevisionId) {
        const key = this.getKey('response', contentRevisionId);
        this.localCache.remove(key);
    }

    removeAllCachedTestData() {
        const keysToKeep = [
            'parentChildTestInfo',
            'parentTime',
            'isInitialIdVerificationCompleted',
            'isRoomScanConfirmed',
            'readinessCheckComplete',
            'lastLocalCacheSanitisationTimestampForCandidateInTest',
        ];
        const allKeys = this.localCache.getAllKeys();
        const prefix = this.getKeyPrefixForTest();
        const keys = this
            .getKeysWithPrefix(allKeys, prefix)
            .filter(key => doesNotIncludeAnyOf(keysToKeep, key));

        keys.forEach((key) => {
            if (this.localCache.get(key)) {
                this.localCache.remove(key);
            }
        });
    }

    getAllQuestionResponses() {
        const allKeys = this.localCache.getAllKeys();
        const prefix = this.getKeyPrefix('response');
        const keys = this.getKeysWithPrefix(allKeys, prefix);
        const responses = keys.map((key) => {
            const cachedResponse = this.localCache.get(key);
            return JSON.parse(cachedResponse);
        });

        return responses;
    }

    setFlaggedItems(flaggedItems) {
        const key = this.getKey('flaggedItems');
        const json = JSON.stringify({ flaggedItems });

        this.localCache.set(key, json);
    }

    getFlaggedItems() {
        const key = this.getKey('flaggedItems');
        const cachedResponse = this.localCache.get(key);

        if (cachedResponse) {
            const { flaggedItems } = JSON.parse(cachedResponse);

            return flaggedItems;
        }

        return [];
    }

    setParentChildTestInfo(info) {
        const key = this.getKey('parentChildTestInfo');
        this.localCache.set(key, JSON.stringify(info));
    }

    getParentChildTestInfo() {
        const key = this.getKey('parentChildTestInfo');
        const info = this.localCache.get(key);
        return info ? JSON.parse(info) : undefined;
    }

    setReadinessCheckComplete(contentRevisionId) {
        const key = this.getKey('readinessCheckComplete');
        this.localCache.set(key, JSON.stringify(contentRevisionId));
    }

    isReadinessCheckComplete(contentRevisionId) {
        const key = this.getKey('readinessCheckComplete');
        const storedReadinessId = this.localCache.get(key);
        return !!storedReadinessId && parseInt(storedReadinessId, 10) === contentRevisionId;
    }

    getMessages() {
        // Key matches key used in student dashboard, loggedInUserId always -1 in dashboard (NotificationListModel.js)
        return this.localCache.get('readNotifications_-1');
    }

    setReadMessages(messages) {
        this.localCache.set('readNotifications_-1', messages);
    }

    setTimeRemaining(timeRemaining) {
        const key = this.getKey('timeRemaining');
        this.localCache.set(key, `${timeRemaining}`);
    }

    getTimeRemaining() {
        const key = this.getKey('timeRemaining');
        const timeRemaining = parseInt(this.localCache.get(key), 10);

        return isNaN(timeRemaining) ? undefined : timeRemaining;
    }

    setParentTime(parentTime) {
        const key = this.getKey('parentTime');
        const json = JSON.stringify(parentTime);
        this.localCache.set(key, json);
    }

    getParentTime() {
        const key = this.getKey('parentTime');

        const parentTime = this.localCache.get(key);
        return parentTime ? JSON.parse(parentTime) : undefined;
    }

    setDeliveryObject(assessmentRunId, deliveryObject) {
        const key = this.getKey(`deliveryObject/${assessmentRunId}`);
        const json = JSON.stringify(deliveryObject);
        this.localCache.set(key, json);
    }

    getDeliveryObject(assessmentRunId) {
        const key = this.getKey(`deliveryObject/${assessmentRunId}`);
        const deliveryObject = this.localCache.get(key);
        return deliveryObject ? JSON.parse(deliveryObject) : undefined;
    }

    removeDeliveryObject(assessmentRunId) {
        const key = this.getKey(`deliveryObject/${assessmentRunId}`);
        this.localCache.remove(key);
    }

    setDeliveryStatus(assessmentRunId, status) {
        const key = this.getKey(`deliveryStatus/${assessmentRunId}`);
        this.localCache.set(key, status);
    }

    getDeliveryStatus(assessmentRunId) {
        const key = this.getKey(`deliveryStatus/${assessmentRunId}`);
        return this.localCache.get(key);
    }

    setBackgroundAudioIntroPlayed(isPlayed) {
        const key = this.getKey('backgroundAudioIntroPlayed');
        this.localCache.set(key, isPlayed);
    }

    isBackgroundAudioIntroPlayed() {
        const key = this.getKey('backgroundAudioIntroPlayed');
        return this.localCache.get(key) === 'true';
    }

    setSectionAudioPosition(audioContentItemId, position) {
        const key = this.getKey('sectionAudioPosition', audioContentItemId);
        this.localCache.set(key, position);
    }

    getSectionAudioPosition(audioContentItemId) {
        const key = this.getKey('sectionAudioPosition', audioContentItemId);
        const position = parseFloat(this.localCache.get(key));

        return isNaN(position) ? undefined : position;
    }

    setSectionAudioStatus(audioContentItemId, status) {
        const key = this.getKey('sectionAudioStatus', audioContentItemId);
        this.localCache.set(key, status);
    }

    getSectionAudioStatus(audioContentItemId) {
        const key = this.getKey('sectionAudioStatus', audioContentItemId);
        return this.localCache.get(key);
    }

    setSectionAudioTimesReplayRequested(audioContentItemId, timesPlayed) {
        const key = this.getKey('sectionAudioTimesReplayed', audioContentItemId);
        this.localCache.set(key, timesPlayed);
    }

    getSectionAudioTimesPlayed(audioContentItemId) {
        const key = this.getKey('sectionAudioTimesReplayed', audioContentItemId);
        return parseInt(this.localCache.get(key), 10);
    }

    getAllSectionAudioStatuses() {
        return this.getSectionAudioData('sectionAudioStatus', this.getSectionAudioStatus.bind(this));
    }

    getAllSectionAudioPositions() {
        return this.getSectionAudioData('sectionAudioPosition', this.getSectionAudioPosition.bind(this));
    }

    getAllSectionAudioTimesReplayRequested() {
        return this.getSectionAudioData('sectionAudioTimesReplayed', this.getSectionAudioTimesPlayed.bind(this));
    }

    getSectionAudioData(param, getter) {
        const allKeys = this.localCache.getAllKeys();
        const prefix = this.getKeyPrefix(param);
        const audioStatusKeys = this.getKeysWithPrefix(allKeys, prefix);

        const values = {};
        audioStatusKeys.forEach((key) => {
            const sectionId = key.match(/\d+$/)?.[0];
            if (isNaN(sectionId)) {
                return;
            }

            values[sectionId] = getter(sectionId);
        });

        return values;
    }

    setRoomScanConfirmationStatus(isCompleted) {
        const key = this.getKey('isRoomScanConfirmed');
        if (isCompleted) {
            this.localCache.set(key, isCompleted);
        } else {
            this.localCache.remove(key);
        }
    }

    isRoomScanConfirmed() {
        const key = this.getKey('isRoomScanConfirmed');
        return !!this.localCache.get(key);
    }

    setInitialIdVerificationCompletion(isCompleted) {
        const key = this.getKey('isInitialIdVerificationCompleted');

        if (isCompleted) {
            this.localCache.set(key, isCompleted);
        } else {
            this.localCache.remove(key);
        }
    }

    // Temporary fix for RP-1355 to not do ID card step upon relogin.
    isInitialIdVerificationCompleted() {
        const key = this.getKey('isInitialIdVerificationCompleted');
        return !!this.localCache.get(key);
    }

    setLastLocalCacheSanitisationTimestampForCandidateInTest(dateInMillis) {
        const key = this.getKey('lastLocalCacheSanitisationTimestampForCandidateInTest');
        this.localCache.set(key, dateInMillis);
    }

    getLastLocalCacheSanitisationTimestampForCandidateInTest() {
        const key = this.getKey('lastLocalCacheSanitisationTimestampForCandidateInTest');
        return this.localCache.get(key);
    }

    setQuestionCacheValue(keyName, value) {
        const key = this.getKey(keyName);
        this.localCache.set(key, value);
    }

    removeQuestionCacheValue(keyName) {
        const key = this.getKey(keyName);
        this.localCache.remove(key);
    }

    getQuestionCacheValue(keyName) {
        const key = this.getKey(keyName);
        return this.localCache.get(key);
    }

    getKeyPrefix(type) {
        const keyPrefixForTest = this.getKeyPrefixForTest();
        return `${keyPrefixForTest}${type}/`;
    }

    getKeyPrefixForTest() {
        const { paramName, paramValue } = this.questionSetIdentifier;
        const userId = sessionReader.getUserId();
        const cachePrefix = localCacheService.getCachePrefix();
        return `${cachePrefix}/${userId}/${paramName}/${paramValue}/`;
    }

    getKey(type, id = '_') {
        const prefix = this.getKeyPrefix(type);
        return `${prefix}${id}`;
    }

    getKeysWithPrefix(keys, prefix) {
        return keys.filter(key => key.indexOf(prefix) === 0);
    }

    getLastExposedToTestTimestampObject(assessmentRunId) {
        const key = this.getKey('lastExposedToTestTimestamp', assessmentRunId);
        const lastExposedToTestTimestamp = this.localCache.get(key);
        return lastExposedToTestTimestamp ? JSON.parse(lastExposedToTestTimestamp) : undefined;
    }

    // for testing purpose
    setPwaAppToken(pwaAppToken) {
        this.localCache.set('pwaAppToken', pwaAppToken);
    }

    getPwaAppToken() {
        // It has to be the very same as the key used by IB-5486 when it was stored for the domain.
        const pwaAppToken = this.localCache.get('pwaAppToken');
        if (pwaAppToken) {
            return pwaAppToken;
        }
        return null;
    }

    removePwaAppToken() {
        this.localCache.remove('pwaAppToken');
    }

    createInitialLastExposedTime(assessmentRunId) {
        const key = this.getKey('lastExposedToTestTimestamp', assessmentRunId);

        const currentTimestamp = Date.now();
        const data = {
            lastExposedToTestTimestamp: currentTimestamp,
        };

        this.localCache.set(key, JSON.stringify(data));
    }

    updateLastExposedTime(assessmentRunId) {
        const key = this.getKey('lastExposedToTestTimestamp', assessmentRunId);

        if (!this.localCache.get(key)) {
            this.createInitialLastExposedTime(assessmentRunId);
            return;
        }
        const currentTimestamp = Date.now();
        const data = JSON.parse(this.localCache.get(key));

        data.lastExposedToTestTimestamp = currentTimestamp;

        this.localCache.set(key, JSON.stringify(data));
    }

    setLastVisitedQuestion(assessmentRunId, questionId) {
        const key = this.getKey('lastVisitedQuestion', assessmentRunId);

        const timestampMillis = Date.now();
        const entry = this.localCache.get(key);
        const data = entry ? JSON.parse(entry) : {};

        data.timestampMillis = timestampMillis;
        data.identifier = questionId;

        this.localCache.set(key, JSON.stringify(data));
    }

    getLastVisitedQuestionObject(assessmentRunId) {
        const key = this.getKey('lastVisitedQuestion', assessmentRunId);
        const lastVisitedQuestionKey = this.localCache.get(key);
        return lastVisitedQuestionKey ? JSON.parse(lastVisitedQuestionKey) : undefined;
    }
}

const stateCacheService = {
    getStateCacheInstance() {
        if (instance !== null) {
            return instance;
        }

        instance = new StateCache();

        return instance;
    },
};

export default stateCacheService;
